import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import DestinationCheckboxes, {
  CONNECT_PUBLIC_NOTICE,
  type DestinationSelection,
} from '@/components/documents/DestinationCheckboxes';

export const CATALOG_PLACEMENT_HELP =
  'File Center can change now. Connect and Research Assistant can change after this file has been processed.';

export type CatalogPlacementDraft = {
  file_center: boolean;
  connect: boolean;
  research_assistant: boolean;
  municipality_document_group_id: string;
};

type FolderOption = { id: number; label: string };

type CatalogRowEditorProps = {
  fileCenter: boolean;
  researchAssistant: boolean;
  connect: boolean;
  folderId: number | null;
  folders: FolderOption[];
  onSave: (placement: CatalogPlacementDraft) => Promise<void>;
};

export default function CatalogRowEditor({
  fileCenter,
  researchAssistant,
  connect,
  folderId,
  folders,
  onSave,
}: CatalogRowEditorProps) {
  const [destinations, setDestinations] = useState<DestinationSelection>({
    file_center: fileCenter,
    research_assistant: researchAssistant,
    connect,
  });
  const [folder, setFolder] = useState(folderId == null ? '' : String(folderId));
  const [saving, setSaving] = useState(false);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    try {
      await onSave({
        file_center: destinations.file_center,
        connect: destinations.connect,
        research_assistant: destinations.research_assistant,
        municipality_document_group_id: folder,
      });
    } catch {
      // The caller reports the failure. Keep the draft so it can be corrected.
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={(event) => void save(event)} className="space-y-4" aria-busy={saving}>
      <DestinationCheckboxes
        documentType="hierarchical"
        value={destinations}
        onChange={saving ? () => undefined : setDestinations}
        help={CATALOG_PLACEMENT_HELP}
        connectNotice={CONNECT_PUBLIC_NOTICE}
        connectRequiresResearch
        researchBeforeConnect
      />
      <div className="space-y-2">
        <Label htmlFor="catalog-row-folder">Folder</Label>
        <select
          id="catalog-row-folder"
          value={folder}
          disabled={saving}
          onChange={(event) => setFolder(event.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
        >
          <option value="">No folder</option>
          {folders.map((option) => (
            <option key={option.id} value={String(option.id)}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <Button type="submit" size="sm" disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </form>
  );
}
